import { memo, useCallback, useEffect, useState } from 'react';
import { Buttons, CheckBox, DeprecatedInput } from '../../customcontrols';
import { IsIEFn, Searchingfn } from '../../../utils/helpers';
import { CommodityMap } from '../../../types/commodity';
import NoResult from '../../customcontrols/noresult';
import { Collapse } from 'reactstrap';
import CommodityListItem from '../research/broadcastlist/commoditycodes/commoditycodeedit/commoditylistitem';

interface InstantBidManualCommoditySelectionProps {
  allCommodities: CommodityMap[];
  preSelectedCommodities: CommodityMap[];
  onProceed: (selectedCommodities: CommodityMap[]) => void;
  onCancel: () => void;
}

interface CommodityWithHierarchy extends Partial<CommodityMap> {
  commodityCode?: string;
  commodityGroup?: string;
  commodityCategory?: string;
  contains?: number;
  isSelected?: boolean;
  isPartial?: boolean;
  isExisting?: boolean;
  isDisplay?: boolean;
}

function InstantBidManualCommoditySelection(props: InstantBidManualCommoditySelectionProps) {
  const { allCommodities = [], preSelectedCommodities = [], onProceed, onCancel } = props;
  const [searchText, setSearchText] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [selectedCommodities, setSelectedCommodities] = useState<CommodityMap[]>([]);
  const [searchResults, setSearchResults] = useState<CommodityMap[]>([]);
  const [expandedGroup, setExpandedGroup] = useState('');
  const [expandedCategory, setExpandedCategory] = useState('');

  const normalizeCommodity = useCallback((commodity: CommodityMap): CommodityMap => {
    const formattedCode = commodity.formattedCode || commodity.fullCode || '';
    const codeParts = formattedCode.split('-');

    return {
      ...commodity,
      commodityId: Number(commodity.commodityId),
      formattedCode,
      fullCode: commodity.fullCode || '',
      commodityCode: String(commodity.commodityCode || codeParts[2] || ''),
      commodityDescription: commodity.commodityDescription || '',
      isSelected: true,
    };
  }, []);

  // const getCategoryKey = (commodity: CommodityMap): string => {
  //   const [group = '', category = ''] = commodity.formattedCode?.split('-') || [];
  //   return `${group}-${category}`;
  // };

  // const getCodesUnderCategory = (commodity: CommodityMap): CommodityMap[] => {
  //   const [group = '', category = ''] = commodity.formattedCode?.split('-') || [];

  //   return allCommodities
  //     .filter(item => {
  //       const [itemGroup = '', itemCategory = '', itemCode = ''] =
  //         item.formattedCode?.split('-') || [];
  //       return itemGroup === group && itemCategory === category && itemCode !== '00';
  //     })
  //     .map(normalizeCommodity);
  // };

  const expandPreSelectedCommodities = useCallback(
    (commodities: CommodityMap[]): CommodityMap[] => {
      return commodities.map(normalizeCommodity);
    },
    [normalizeCommodity],
  );

  useEffect(() => {
    setSelectedCommodities(expandPreSelectedCommodities(preSelectedCommodities));
  }, [preSelectedCommodities, expandPreSelectedCommodities]);

  const justAddedCommodities = selectedCommodities.filter(
    selected =>
      !preSelectedCommodities.some(pre => Number(pre.commodityId) === selected.commodityId),
  );

  const previouslySelectedCommodities = selectedCommodities.filter(selected =>
    preSelectedCommodities.some(pre => Number(pre.commodityId) === selected.commodityId),
  );
  const isCategoryBold = (category: CommodityMap): boolean => {
    const [group = '', categoryCode = ''] = category.formattedCode?.split('-') || [];

    return selectedCommodities.some(item => {
      const [itemGroup = '', itemCategory = '', itemCode = ''] =
        item.formattedCode?.split('-') || [];

      return itemGroup === group && itemCategory === categoryCode && itemCode !== '00';
    });
  };

  const isGroupBold = (group: CommodityMap): boolean => {
    const [groupCode = ''] = group.formattedCode?.split('-') || [];

    return selectedCommodities.some(item => {
      const [itemGroup = '', itemCategory = '', itemCode = ''] =
        item.formattedCode?.split('-') || [];

      return itemGroup === groupCode && itemCategory !== '000';
    });
  };
  const handleToggleCommodity = useCallback(
    (commodity: CommodityMap) => {
      const normalizedCommodity = normalizeCommodity(commodity);

      setSelectedCommodities(prev => {
        let updated: CommodityMap[] = [...prev];

        const exists = updated.some(item => item.commodityId === normalizedCommodity.commodityId);

        const [group = '', category = '', code = ''] =
          normalizedCommodity.formattedCode?.split('-') || [];

        const isParentCode = normalizedCommodity.commodityCode === '00' || code === '00';
        if (exists) {
          updated = updated.filter(item => item.commodityId !== normalizedCommodity.commodityId);
        } else {
          if (isParentCode) {
            updated = updated.filter(item => {
              const [itemGroup = '', itemCategory = '', itemCode = ''] =
                item.formattedCode?.split('-') || [];

              return !(itemGroup === group && itemCategory === category && itemCode !== '00');
            });
            updated.push(normalizedCommodity);
          } else {
            updated = updated.filter(item => {
              const [itemGroup = '', itemCategory = '', itemCode = ''] =
                item.formattedCode?.split('-') || [];

              return !(itemGroup === group && itemCategory === category && itemCode === '00');
            });
            updated.push(normalizedCommodity);
          }
        }

        return Array.from(new Map(updated.map(item => [item.commodityId, item])).values());
      });
    },
    [normalizeCommodity],
  );

  const searchCommodityCodes = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    if (searchText.length >= 3) {
      setShowSearch(true);
      const searchresult = Searchingfn(
        allCommodities,
        ['commodityDescription', 'formattedCode'],
        searchText,
      );
      setSearchResults(searchresult);
    }
    if (!searchText || searchText.length === 0) {
      setShowSearch(false);
      setSearchResults([]);
    }
  };

  const onChangeSearchCommodityCodes = (value: string) => {
    setSearchText(value);
    if (value.length >= 3) {
      setShowSearch(true);
      const searchresult = Searchingfn(
        allCommodities,
        ['commodityDescription', 'formattedCode'],
        value,
      );
      setSearchResults(searchresult);
    }
    if (!value || value.length === 0) {
      setShowSearch(false);
      setSearchResults([]);
    }
  };

  const handleClear = () => {
    setShowSearch(false);
    setSearchText('');
    setSearchResults([]);
  };

  const handleProceed = () => {
    onProceed(selectedCommodities);
  };

  const isCommoditySelected = (commodity: CommodityMap): boolean => {
    const [group = '', category = '', code = ''] = commodity.formattedCode?.split('-') || [];
    const isParentCode = commodity.commodityCode === '00' || code === '00';
    const directlySelected = selectedCommodities.some(
      item => item.commodityId === commodity.commodityId,
    );

    if (directlySelected) {
      return true;
    }
    if (!isParentCode) {
      const parentSelected = selectedCommodities.some(item => {
        const [itemGroup = '', itemCategory = '', itemCode = ''] =
          item.formattedCode?.split('-') || [];

        return itemGroup === group && itemCategory === category && itemCode === '00';
      });

      return parentSelected;
    }

    return false;
  };

  const isParentIndeterminate = (commodity: CommodityMap): boolean => {
    const [group = '', category = '', code = ''] = commodity.formattedCode?.split('-') || [];

    const isParentCode = commodity.commodityCode === '00' || code === '00';

    if (!isParentCode) {
      return false;
    }

    const parentSelected = selectedCommodities.some(
      item => item.commodityId === commodity.commodityId,
    );

    if (parentSelected) {
      return false;
    }

    const anyChildSelected = selectedCommodities.some(item => {
      const [itemGroup = '', itemCategory = '', itemCode = ''] =
        item.formattedCode?.split('-') || [];

      return itemGroup === group && itemCategory === category && itemCode !== '00';
    });

    return anyChildSelected;
  };

  const handleCommodityListItemDelete = (
    isSelected: boolean,
    commodity: CommodityMap,
    type: string,
  ) => {
    handleToggleCommodity(commodity);
  };

  const getCommodityGroups = (): CommodityWithHierarchy[] => {
    const groupsMap = new Map<string, CommodityWithHierarchy>();

    allCommodities.forEach((commodity: any) => {
      const codeParts = commodity.formattedCode?.split('-') || [];
      const groupCode = codeParts[0];
      const categoryCode = codeParts[1];

      if (groupCode && categoryCode === '000') {
        const key = groupCode;
        if (!groupsMap.has(key)) {
          groupsMap.set(key, {
            ...commodity,
            commodityGroup: groupCode,
            commodityCategory: categoryCode,
          });
        }
      }
    });

    return Array.from(groupsMap.values());
  };

  const getCategoriesByGroup = (groupCode: string): CommodityWithHierarchy[] => {
    const categoriesMap = new Map<string, CommodityWithHierarchy>();

    allCommodities.forEach((commodity: any) => {
      const codeParts = commodity.formattedCode?.split('-') || [];
      const group = codeParts[0];
      const category = codeParts[1];
      const code = codeParts[2];

      if (group === groupCode && code === '00' && category !== '000') {
        const key = category;
        if (!categoriesMap.has(key)) {
          categoriesMap.set(key, {
            ...commodity,
            commodityGroup: group,
            commodityCategory: category,
          });
        }
      }
    });

    return Array.from(categoriesMap.values());
  };

  const getCodesByCategory = (
    groupCode: string,
    categoryCode: string,
  ): CommodityWithHierarchy[] => {
    return allCommodities.filter((commodity: any) => {
      const codeParts = commodity.formattedCode?.split('-') || [];
      const group = codeParts[0];
      const category = codeParts[1];
      const code = codeParts[2];

      return group === groupCode && category === categoryCode && code !== '00';
    });
  };

  const commodityGroups = getCommodityGroups();
  const categoriesForGroup = expandedGroup ? getCategoriesByGroup(expandedGroup) : [];

  return (
    <div className='container m-auto'>
      <div className='row commoditylist instantBidManualSelection'>
        <div className='col-lg-9'>
          <div className='innerColIndent'>
            {IsIEFn() ? (
              <form>
                <DeprecatedInput
                  type='text'
                  placeholder='Search Commodity Codes ( Enter minimum 3 characters to search )'
                  label='Search'
                  value={searchText}
                  handleChange={(name: string, value: string) => setSearchText(value)}
                  optional={'e.g "Administrative" or "001-000-00"'}
                  parentClass='mb-0'
                />
                <div className='d-flex justify-content-end mb-3'>
                  <Buttons
                    classNames='bttn-secondary mt-2 small'
                    text='Clear'
                    title='Clear'
                    type='button'
                    onClick={handleClear}
                  />
                  <Buttons
                    classNames='bttn-primary mt-2 ml-2 small'
                    text='Search'
                    title='Search'
                    type='submit'
                    onClick={searchCommodityCodes}
                  />
                </div>
              </form>
            ) : (
              <DeprecatedInput
                type='search'
                placeholder='Search Commodity Codes ( Enter minimum 3 characters to search )'
                label='Search'
                value={searchText}
                handleChange={(name: string, value: string) => onChangeSearchCommodityCodes(value)}
                optional={'e.g "Administrative" or "001-000-00"'}
                parentClass='mb-0'
              />
            )}
          </div>

          {showSearch ? (
            <>
              {searchResults.length > 0 ? (
                <div className='innerColIndent mt-3'>
                  <h4>Commodity Codes Search Result ({searchResults.length})</h4>
                  {searchResults.map((code, index) => {
                    const isChecked = isCommoditySelected(code);
                    const isInactive =
                      code?.fullCode?.endsWith('000-00') || code?.formattedCode?.endsWith('000-00');

                    return (
                      <div key={index} className='quotes-details'>
                        <h6>
                          <i>
                            <CheckBox
                              disabled={isInactive}
                              indeterminate={isParentIndeterminate(code)}
                              disabledText={
                                isInactive
                                  ? 'Top level commodity codes cannot be added. Try searching for a more specific code.'
                                  : ''
                              }
                              handleChecked={() => handleToggleCommodity(code)}
                              checked={isChecked}
                            />
                          </i>
                          {code.commodityDescription} [{code.formattedCode}]
                        </h6>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className='innerColIndent mt-3'>
                  <NoResult message='No Commodities Found' />
                </div>
              )}
            </>
          ) : (
            <>
              {commodityGroups.length > 0 ? (
                <div className='innerColIndent mt-3'>
                  <h4>Commodity Codes List</h4>
                  {commodityGroups.map((group: any, groupindex: number) => (
                    <div className='accordin-band' key={groupindex}>
                      <div
                        className='accordin-title'
                        onClick={() => {
                          const groupCode = group.commodityGroup;
                          if (expandedGroup === groupCode) {
                            setExpandedGroup('');
                          } else {
                            setExpandedGroup(groupCode);
                            setExpandedCategory('');
                          }
                        }}
                      >
                        <p>
                          {isGroupBold(group) ? (
                            <strong>
                              {group.commodityDescription} {group.formattedCode}
                            </strong>
                          ) : (
                            <>
                              {group.commodityDescription} {group.formattedCode}
                            </>
                          )}
                        </p>
                        <i
                          className={
                            'mdi ' +
                            (expandedGroup === group.commodityGroup
                              ? 'mdi-chevron-down-circle-outline'
                              : 'mdi-chevron-right-circle-outline')
                          }
                        />
                      </div>

                      {expandedGroup === group.commodityGroup && categoriesForGroup.length > 0 && (
                        <>
                          {categoriesForGroup.map((category: any, categoryindex: number) => {
                            const categoryCodesCount = getCodesByCategory(
                              group.commodityGroup,
                              category.commodityCategory,
                            ).length;

                            return (
                              <Collapse
                                key={categoryindex}
                                isOpen={expandedGroup === group.commodityGroup}
                              >
                                <div className='quotes-details'>
                                  <div className='accordin-band'>
                                    <div className='accordin-title'>
                                      <p>
                                        <i>
                                          <CheckBox
                                            handleChecked={() => handleToggleCommodity(category)}
                                            checked={isCommoditySelected(category)}
                                            indeterminate={isParentIndeterminate(category)}
                                            disabled={category?.fullCode?.endsWith('000-00')}
                                          />
                                        </i>
                                        <span
                                          onClick={() => {
                                            if (expandedCategory === category.commodityCategory) {
                                              setExpandedCategory('');
                                            } else {
                                              setExpandedCategory(category.commodityCategory);
                                            }
                                          }}
                                        >
                                          {isCategoryBold(category) ||
                                          isCommoditySelected(category) ? (
                                            <strong>
                                              {category.commodityDescription} ({categoryCodesCount}{' '}
                                              Codes)
                                              {category.formattedCode}
                                            </strong>
                                          ) : (
                                            <>
                                              {category.commodityDescription} ({categoryCodesCount}{' '}
                                              Codes)
                                              {category.formattedCode}
                                            </>
                                          )}
                                        </span>
                                      </p>
                                      <i
                                        className={
                                          'mdi ' +
                                          (expandedCategory === category.commodityCategory
                                            ? 'mdi-chevron-down-circle-outline'
                                            : 'mdi-chevron-right-circle-outline')
                                        }
                                        onClick={() => {
                                          if (expandedCategory === category.commodityCategory) {
                                            setExpandedCategory('');
                                          } else {
                                            setExpandedCategory(category.commodityCategory);
                                          }
                                        }}
                                      />
                                    </div>

                                    {expandedCategory === category.commodityCategory &&
                                      getCodesByCategory(
                                        group.commodityGroup,
                                        category.commodityCategory,
                                      ).length > 0 && (
                                        <>
                                          {getCodesByCategory(
                                            group.commodityGroup,
                                            category.commodityCategory,
                                          ).map((code: any, codeindex: number) => (
                                            <Collapse
                                              key={codeindex}
                                              isOpen={
                                                expandedCategory === category.commodityCategory
                                              }
                                            >
                                              <div className='quotes-details'>
                                                <h6>
                                                  <i>
                                                    <CheckBox
                                                      handleChecked={() =>
                                                        handleToggleCommodity(code)
                                                      }
                                                      checked={isCommoditySelected(code)}
                                                    />
                                                  </i>
                                                  {isCommoditySelected(code) ? (
                                                    <strong>
                                                      {code.commodityDescription} [
                                                      {code.formattedCode}]
                                                    </strong>
                                                  ) : (
                                                    <>
                                                      {code.commodityDescription} [
                                                      {code.formattedCode}]
                                                    </>
                                                  )}
                                                </h6>
                                              </div>
                                            </Collapse>
                                          ))}
                                        </>
                                      )}
                                  </div>
                                </div>
                              </Collapse>
                            );
                          })}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  {allCommodities.length > 0 ? (
                    <div className='innerColIndent mt-3'>
                      <h4>Commodity Codes List ({allCommodities.length})</h4>
                      <div className='innerColIndent'>
                        {allCommodities.map((code, index) => {
                          const isChecked = isCommoditySelected(code);
                          const isInactive = code?.fullCode?.endsWith('000-00');

                          return (
                            <div key={index} className='quotes-details'>
                              <h6>
                                <i>
                                  <CheckBox
                                    disabled={isInactive}
                                    indeterminate={isParentIndeterminate(code)}
                                    disabledText={
                                      isInactive
                                        ? 'Top level commodity codes cannot be added. Try searching for a more specific code.'
                                        : ''
                                    }
                                    handleChecked={() => handleToggleCommodity(code)}
                                    checked={isChecked}
                                  />
                                </i>
                                {code.commodityDescription} [{code.formattedCode}]
                              </h6>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className='innerColIndent mt-3'>
                      <NoResult message='No Commodities Available' />
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <div className='col-lg-3'>
          <div className='action-buttons mt-4'>
            <Buttons
              classNames='bttn-secondary btn-block mb-2'
              text='Cancel'
              title='Cancel'
              type='button'
              onClick={onCancel}
            />
            <Buttons
              classNames='bttn-primary btn-block'
              text='Proceed'
              title='Proceed with Selected Commodities'
              type='button'
              onClick={handleProceed}
              disable={selectedCommodities.length === 0}
            />
          </div>
          <div className='selected-commodities-panel'>
            {justAddedCommodities.length > 0 && (
              <div className='mt-4 pt-3 border-top'>
                <h5>
                  Just Added{' '}
                  {justAddedCommodities && justAddedCommodities.length > 0
                    ? `(${justAddedCommodities.length})`
                    : ''}
                </h5>
                <CommodityListItem
                  SetSelectedCommodity={handleCommodityListItemDelete}
                  listings={justAddedCommodities}
                />
              </div>
            )}

            {previouslySelectedCommodities.length > 0 && (
              <div className='mt-3 pt-3 border-top'>
                <h5>
                  Previously Selected Codes{' '}
                  {previouslySelectedCommodities && previouslySelectedCommodities.length > 0
                    ? `(${previouslySelectedCommodities.length})`
                    : ''}
                </h5>
                <CommodityListItem
                  SetSelectedCommodity={handleCommodityListItemDelete}
                  listings={previouslySelectedCommodities}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default memo(InstantBidManualCommoditySelection);
